(() => {
  const ROOT_SELECTOR = '[data-gm-focus-root]';
  const CTA_SELECTOR = 'a[data-gm-focus-intro-cta]';
  const SIDE_QUESTS_ZOOM_SELECTOR = '[data-gm-focus-side-quests-enlarge]';
  const SIDE_QUESTS_ZOOM_QUERY = '(max-width: 899px)';

  const initializeRoot = (root) => {
    if (root.dataset.gmFocusInitialized === 'true') return;
    root.dataset.gmFocusInitialized = 'true';

    const introductoryButtons = Array.from(root.querySelectorAll(CTA_SELECTOR));
    const errorRegions = Array.from(root.querySelectorAll('[data-gm-focus-form-error]'));
    let submissionActive = false;

    const clearErrors = () => {
      errorRegions.forEach((region) => {
        const message = region.querySelector('[data-gm-focus-error-message]');
        if (message) message.textContent = '';
        region.hidden = true;
      });
    };

    const showError = (messageText) => {
      errorRegions.forEach((region) => {
        const message = region.querySelector('[data-gm-focus-error-message]');
        if (message) message.textContent = `${messageText} `;
        region.hidden = false;
      });
    };

    const setLocked = (locked) => {
      submissionActive = locked;
      introductoryButtons.forEach((button) => {
        if (locked) {
          button.setAttribute('aria-busy', 'true');
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
        }
      });
    };

    introductoryButtons.forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();

        if (submissionActive) return;

        clearErrors();

        const variantId = Number(button.dataset.variantId);
        const cartUrl = button.dataset.cartUrl;
        const cartAddUrl = button.dataset.cartAddUrl;
        const discountCartUrl = button.dataset.discountCartUrl;

        if (!variantId || !cartUrl || !cartAddUrl || !discountCartUrl) {
          showError('The introductory offer could not be prepared.');
          return;
        }

        setLocked(true);

        try {
          const cartResponse = await fetch(cartUrl, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin'
          });
          const cart = await cartResponse.json().catch(() => ({}));

          if (!cartResponse.ok || !Array.isArray(cart.items)) {
            throw new Error('Your cart could not be checked.');
          }

          const alreadyInCart = cart.items.some((item) => {
            const itemVariantId = Number(item.variant_id || item.id);
            return itemVariantId === variantId && !item.selling_plan_allocation;
          });

          if (!alreadyInCart) {
            const addResponse = await fetch(cartAddUrl, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              credentials: 'same-origin',
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1 }]
              })
            });
            const payload = await addResponse.json().catch(() => ({}));

            if (!addResponse.ok) {
              throw new Error(payload.description || payload.message || 'This carton could not be added to your cart.');
            }
          }

          window.location.assign(discountCartUrl);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'This carton could not be added to your cart.';
          setLocked(false);
          showError(`${message} Please try again or view the regular product page.`);
        }
      });
    });

    const sideQuestsZoom = root.querySelector(SIDE_QUESTS_ZOOM_SELECTOR);

    if (sideQuestsZoom) {
      const zoomMedia = window.matchMedia(SIDE_QUESTS_ZOOM_QUERY);
      const originalRole = sideQuestsZoom.getAttribute('role');
      const originalLabel = sideQuestsZoom.getAttribute('aria-label');
      let zoomOpen = false;

      const updateZoomSemantics = () => {
        if (zoomMedia.matches) {
          sideQuestsZoom.setAttribute('role', 'button');
          sideQuestsZoom.setAttribute('aria-label', 'Tap to enlarge');
          sideQuestsZoom.setAttribute('aria-haspopup', 'dialog');
        } else {
          sideQuestsZoom.setAttribute('role', originalRole || 'region');
          sideQuestsZoom.setAttribute('aria-label', originalLabel || '');
          sideQuestsZoom.removeAttribute('aria-haspopup');
          sideQuestsZoom.removeAttribute('aria-controls');
        }
      };

      const openSideQuestsZoom = (event) => {
        if (!zoomMedia.matches || zoomOpen || typeof window.theme?.LoadPhotoswipe !== 'function') return;

        event.preventDefault();
        zoomOpen = true;

        const imageSrc = sideQuestsZoom.dataset.imageSrc;
        const imageWidth = Number(sideQuestsZoom.dataset.imageWidth);
        const imageHeight = Number(sideQuestsZoom.dataset.imageHeight);
        const pswpElement = document.querySelector('.pswp');
        const openingScrollX = window.scrollX;
        const openingScrollY = window.scrollY;

        if (!imageSrc || !imageWidth || !imageHeight || !pswpElement) {
          zoomOpen = false;
          return;
        }

        if (!pswpElement.id) pswpElement.id = 'gm-focus-side-quests-zoom';
        pswpElement.setAttribute('aria-labelledby', 'gm-focus-side-quests-title');
        pswpElement.setAttribute('aria-modal', 'true');
        sideQuestsZoom.setAttribute('aria-controls', pswpElement.id);
        window.a11y.lastElement = sideQuestsZoom;

        const options = {
          history: false,
          focus: false,
          index: 0,
          mainClass: 'pswp-zoom-gallery pswp-zoom-gallery--single',
          showHideOpacity: true,
          showAnimationDuration: 150,
          hideAnimationDuration: 250,
          closeOnScroll: false,
          closeOnVerticalDrag: false,
          captionEl: false,
          closeEl: true,
          tapToClose: false,
          clickToCloseNonZoomable: false,
          maxSpreadZoom: 2,
          loop: false,
          spacing: 0,
          allowPanToNext: false,
          pinchToClose: false,
          escKey: true,
          arrowKeys: false,
          getThumbBoundsFn: () => {
            const bounds = sideQuestsZoom.getBoundingClientRect();
            return {
              x: bounds.left,
              y: bounds.top + openingScrollY,
              w: bounds.width
            };
          }
        };

        const loader = new window.theme.LoadPhotoswipe([
          {
            src: imageSrc,
            w: imageWidth,
            h: imageHeight,
            msrc: imageSrc
          }
        ], options);

        const connectPopup = (attempt = 0) => {
          if (!loader.popup) {
            if (attempt < 120) window.requestAnimationFrame(() => connectPopup(attempt + 1));
            return;
          }

          const popup = loader.popup;
          let initialScaleApplied = false;

          const applyReadableScale = () => {
            if (initialScaleApplied || !popup.currItem?.initialZoomLevel) return;
            const targetZoom = Math.max(popup.currItem.initialZoomLevel, Math.min(900 / imageWidth, 1));
            popup.zoomTo(targetZoom, {x: window.innerWidth / 2, y: window.innerHeight / 2}, 0);
            initialScaleApplied = true;
          };

          popup.listen('initialZoomInEnd', applyReadableScale);
          window.setTimeout(applyReadableScale, 450);

          window.setTimeout(() => {
            const closeButton = pswpElement.querySelector('.pswp__custom-close');
            if (closeButton) window.theme.a11y.trapFocus(pswpElement, {elementToFocus: closeButton});
          }, 200);

          popup.listen('close', () => {
            zoomOpen = false;
            window.setTimeout(() => {
              sideQuestsZoom.focus({preventScroll: true});
              window.scrollTo({left: openingScrollX, top: openingScrollY, behavior: 'auto'});
              pswpElement.removeAttribute('aria-labelledby');
              pswpElement.removeAttribute('aria-modal');
            });
          });
        };

        connectPopup();
      };

      sideQuestsZoom.addEventListener('click', openSideQuestsZoom);
      sideQuestsZoom.addEventListener('keydown', (event) => {
        if (event.code === 'Enter' || event.code === 'Space') openSideQuestsZoom(event);
      });

      if (typeof zoomMedia.addEventListener === 'function') {
        zoomMedia.addEventListener('change', updateZoomSemantics);
      } else {
        zoomMedia.addListener(updateZoomSemantics);
      }

      updateZoomSemantics();
    }
  };

  const initialize = (scope = document) => {
    document.body.classList.add('gm-focus-view');
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeRoot);
  };

  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
